import { cn } from "@/lib/utils"

interface Message {
  senderType: 'user' | 'artist' | 'assistant'
  content: string
  timestamp: string | { seconds: number; nanoseconds: number }
  isRead: boolean
  type?: 'text' | 'image' | 'notification' | 'booking_selection' | 'simple_availability'
  notificationSent?: boolean
  notification?: {
    notificationId: string
    status: 'pending' | 'sent' | 'received' | 'clicked' | 'dismissed'
    sentAt?: Date
    receivedAt?: Date
    clickedAt?: Date
    dismissedAt?: Date
    deliveryAttempts?: number
    fcmResponseId?: string
  }
}

interface ChatStatusFlowProps {
  waitingStatus?: 'waiting_user' | 'waiting_artist' | null | string
  platformExitLikelihood?: number | null
  lastMessage?: Message | null
}

export function ChatStatusFlow({
  waitingStatus = null,
  platformExitLikelihood,
  lastMessage
}: ChatStatusFlowProps) {
  // Determine if exit risk is critical (>= 40%)
  const hasHighExitRisk = platformExitLikelihood != null && platformExitLikelihood >= 40

  // Type guard for waiting status
  const validWaitingStatus = waitingStatus === 'waiting_user' || waitingStatus === 'waiting_artist'
    ? waitingStatus
    : null

  // Determine notification status
  let notificationStatus = 'none'
  if (lastMessage?.notification) {
    if (lastMessage.notification.status === 'received') {
      notificationStatus = 'received'
    } else if (lastMessage.notification.status === 'sent') {
      notificationStatus = 'sent'
    } else if (['pending', 'clicked', 'dismissed'].includes(lastMessage.notification.status)) {
      notificationStatus = lastMessage.notification.status
    }
  } else if (lastMessage?.notificationSent !== undefined) {
    notificationStatus = lastMessage.notificationSent ? 'sent' : 'disabled'
  }

  // Determine read status
  const isMessageRead = lastMessage?.isRead ?? true
  const isAssistantMessage = lastMessage?.senderType === 'assistant'

  const pill = (className: string, label: string) => (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold leading-tight tabular-nums shadow-sm ring-1 ring-inset ring-black/10 sm:text-[11px] dark:ring-white/15',
        className
      )}
    >
      {label}
    </span>
  )

  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
      {lastMessage && validWaitingStatus && (
        <>
          {notificationStatus === 'received'
            ? pill(
                'bg-emerald-100 text-emerald-900 ring-emerald-500/35 dark:bg-emerald-950/90 dark:text-emerald-100 dark:ring-emerald-400/40',
                'Push ✓'
              )
            : notificationStatus === 'sent'
              ? pill(
                  'bg-sky-100 text-sky-950 ring-sky-500/40 dark:bg-sky-950/90 dark:text-sky-50 dark:ring-sky-400/45',
                  'Push sent'
                )
              : notificationStatus === 'disabled'
                ? pill(
                    'bg-zinc-200/90 text-zinc-800 ring-zinc-500/30 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-zinc-500/40',
                    'Push off'
                  )
                : notificationStatus === 'none'
                  ? pill(
                      'bg-zinc-100 text-zinc-700 ring-zinc-400/25 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-500/35',
                      'No push'
                    )
                  : pill(
                      'bg-rose-100 text-rose-900 ring-rose-500/35 dark:bg-rose-950/90 dark:text-rose-100 dark:ring-rose-400/40',
                      `Push: ${notificationStatus}`
                    )}

          {!isAssistantMessage && (
            <>
              {isMessageRead
                ? pill(
                    'bg-emerald-50 text-emerald-900 ring-emerald-500/25 dark:bg-emerald-950/80 dark:text-emerald-100 dark:ring-emerald-400/35',
                    'Read'
                  )
                : pill(
                    'bg-amber-100 text-amber-950 ring-amber-500/40 dark:bg-amber-950/85 dark:text-amber-50 dark:ring-amber-400/45',
                    'Unread'
                  )}
            </>
          )}

          {validWaitingStatus === 'waiting_artist'
            ? pill(
                'bg-blue-100 text-blue-950 ring-blue-500/35 dark:bg-blue-950/90 dark:text-blue-50 dark:ring-blue-400/45',
                'Wait artist'
              )
            : validWaitingStatus === 'waiting_user'
              ? pill(
                  'bg-orange-100 text-orange-950 ring-orange-500/40 dark:bg-orange-950/90 dark:text-orange-50 dark:ring-orange-400/45',
                  'Wait user'
                )
              : null}
        </>
      )}

      {hasHighExitRisk && (
        <span
          className={cn(
            'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold leading-tight tabular-nums shadow-sm ring-1 ring-inset ring-black/10 sm:text-[11px] dark:ring-white/15',
            platformExitLikelihood! >= 70
              ? 'bg-red-100 text-red-950 ring-red-500/40 dark:bg-red-950/90 dark:text-red-50 dark:ring-red-400/45'
              : 'bg-orange-100 text-orange-950 ring-orange-500/35 dark:bg-orange-950/90 dark:text-orange-50 dark:ring-orange-400/40'
          )}
        >
          Exit {Math.round(platformExitLikelihood!)}%
        </span>
      )}
    </div>
  )
}
